import type { StyleSpecification } from 'maplibre-gl'

/**
 * The basemap registry. Adding a provider (a self-hosted PMTiles build, say)
 * is one entry here; nothing else in the app knows a provider by name.
 *
 * OpenFreeMap style URLs verified 2026-09-15 against
 * https://openfreemap.org/quick_start/ — its planet tiles stop at zoom 14 and
 * overzoom cleanly, so 16 is a safe display maximum. CARTO's raster tiles are
 * 256 px and stop at 12.
 */

export type BasemapId = 'positron' | 'dark' | 'liberty' | 'carto-voyager'

/**
 * What is stored: a basemap, or `auto`, which follows the theme (Phase 10 plan
 * §2 row 6). `auto` is the default, and `setBasemap` is still the only writer,
 * so a stored id is only ever somebody's explicit choice and keeps winning.
 */
export type BasemapChoice = 'auto' | BasemapId

export const DEFAULT_BASEMAP: BasemapChoice = 'auto'

/** What `auto` draws under each theme. */
const THEME_BASEMAP: Record<BasemapTone, BasemapId> = { light: 'positron', dark: 'dark' }

/**
 * Whether a basemap is light or dark ground. The overlays that draw in ink
 * (isobars, height contours, barbs) take their colour from it, because black
 * lines on the dark map are not there at all.
 */
export type BasemapTone = 'light' | 'dark'

export interface Basemap {
  id: BasemapId
  label: string
  maxZoom: number
  tone: BasemapTone
  /** Only offered once /api/config hands us a key; unkeyed tiles are watermarked. */
  requiresCartoKey: boolean
  style(cartoKey: string): string | StyleSpecification
}

const OFM = 'https://tiles.openfreemap.org/styles'

/** The Phase 0 style, kept verbatim so the CARTO fallback looks unchanged. */
function cartoVoyager(cartoKey: string): StyleSpecification {
  const suffix = cartoKey ? `?key=${encodeURIComponent(cartoKey)}` : ''
  return {
    version: 8,
    sources: {
      carto: {
        type: 'raster',
        tiles: ['a', 'b', 'c'].map(
          (s) =>
            `https://${s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png${suffix}`,
        ),
        tileSize: 256,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      },
    },
    layers: [{ id: 'carto', type: 'raster', source: 'carto' }],
  }
}

export const BASEMAPS: readonly Basemap[] = [
  {
    id: 'positron',
    tone: 'light',
    label: 'Positron (light)',
    maxZoom: 16,
    requiresCartoKey: false,
    style: () => `${OFM}/positron`,
  },
  {
    id: 'dark',
    tone: 'dark',
    label: 'Dark',
    maxZoom: 16,
    requiresCartoKey: false,
    style: () => `${OFM}/dark`,
  },
  {
    id: 'liberty',
    tone: 'light',
    label: 'Liberty (detailed)',
    maxZoom: 16,
    requiresCartoKey: false,
    style: () => `${OFM}/liberty`,
  },
  {
    id: 'carto-voyager',
    tone: 'light',
    label: 'CARTO Voyager (raster)',
    maxZoom: 12,
    requiresCartoKey: true,
    style: cartoVoyager,
  },
]

export function getBasemap(id: string): Basemap {
  return BASEMAPS.find((b) => b.id === id) ?? BASEMAPS[0]
}

export function isBasemapId(id: unknown): id is BasemapId {
  return typeof id === 'string' && BASEMAPS.some((b) => b.id === id)
}

export function isBasemapChoice(id: unknown): id is BasemapChoice {
  return id === 'auto' || isBasemapId(id)
}

/**
 * The basemap to draw for a stored choice under a theme. `ground` is what the
 * layers on the map need under them (`groundFor` in the registry), and it
 * beats the theme but never an explicit choice: Match theme is a default, and
 * a default may bend to what is drawn over it where a choice may not.
 */
export function resolveBasemap(
  choice: BasemapChoice,
  theme: BasemapTone,
  ground: BasemapTone | null = null,
): BasemapId {
  return choice === 'auto' ? THEME_BASEMAP[ground ?? theme] : choice
}

/** CARTO is hidden entirely without a key rather than offered watermarked. */
export function availableBasemaps(cartoKey: string): Basemap[] {
  return BASEMAPS.filter((b) => !b.requiresCartoKey || cartoKey !== '')
}
